// CollabPanel.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Avatar, Typography, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export default function CollabPanel({
  author,
  collaborators,
  onInviteClick,
  onlineUsers,
  onRemoveCollaborator,
}) {
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const handleOpenRemoveDialog = (user) => {
    if (!onRemoveCollaborator || user.isAuthor) return;
    setRemoveTarget({ id: user._id || user.id, username: user.username || user.name || 'Unknown' });
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    await onRemoveCollaborator(removeTarget.id);
    setRemoving(false);
    setRemoveTarget(null);
  };

  const [profilePictures, setProfilePictures] = useState({});

  const allUsers = React.useMemo(() => {
    const users = [];
    if (author) users.push({ ...author, isAuthor: true });
    if (Array.isArray(collaborators)) users.push(...collaborators);
    return users;
  }, [author, collaborators]);

  const onlineIdSet = new Set(
    Array.isArray(onlineUsers)
      ? onlineUsers.map((u) => u && (u.userId || u.id || u._id)).filter(Boolean)
      : []
  );

  // Fetch profile pictures for all users
  useEffect(() => {
    const fetchProfilePictures = async () => {
      if (allUsers.length === 0) return;

      const pictures = {};
      for (const user of allUsers) {
        const userId = user._id || user.id;
        if (!userId || pictures[userId]) continue;

        try {
          const token = localStorage.getItem('access_token') || localStorage.getItem('rv_access_token');
          const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

          const response = await fetch(`${API_BASE}/api/profile/user/${userId}/picture-url`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.url) {
              pictures[userId] = data.url;
            }
          }
        } catch (error) {
          console.error(`Failed to fetch profile picture for ${userId}:`, error);
        }
      }

      setProfilePictures(pictures);
    };

    fetchProfilePictures();
  }, [allUsers]);

  const getAvatarLetter = (username) => {
    return (username || 'U')[0].toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      {/* Invite Button */}
      <Button
        variant="contained"
        onClick={onInviteClick}
        startIcon={<PersonAddIcon />}
        sx={{
          width: '100%',
          bgcolor: '#F5D76E',
          color: '#1A1A1A',
          '&:hover': {
            bgcolor: '#D4AF37',
          },
          textTransform: 'none',
          fontWeight: 600,
          py: 1.5,
          mb: 2,
        }}
      >
        New Request
      </Button>

      {/* Collaborators List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          py: 1,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
          },
        }}
      >
        {allUsers.map((user) => {
          const userId = user?._id || user?.id;
          const isOnline = userId ? onlineIdSet.has(String(userId)) : false;
          const username = user.username || user.name || 'User';
          const profilePicUrl = profilePictures[userId];
          const isAuthor = user.isAuthor;

          return (
            <Box
              key={userId || Math.random()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                border: isAuthor ? '2px solid #F5D76E' : '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={profilePicUrl || undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: isOnline ? '#667eea' : '#6B5B95',
                    border: isAuthor 
                      ? '2px solid #F5D76E' 
                      : isOnline 
                      ? '2px solid #4caf50' 
                      : 'none',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {!profilePicUrl && getAvatarLetter(username)}
                </Avatar>
                {isOnline && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: 10,
                      height: 10,
                      bgcolor: '#22C55E',
                      border: '2px solid #4A3F8C',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: isAuthor ? 600 : 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {username}
                </Typography>
                {isAuthor && (
                  <Typography
                    sx={{
                      color: '#F5D76E',
                      fontSize: '0.65rem',
                      fontWeight: 500,
                    }}
                  >
                    Author
                  </Typography>
                )}
              </Box>
              {!isAuthor && onRemoveCollaborator && (
                <IconButton
                  size="small"
                  onClick={() => handleOpenRemoveDialog(user)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': {
                      color: '#ef4444',
                      bgcolor: 'rgba(239, 68, 68, 0.1)',
                    },
                  }}
                  title="Remove collaborator"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Box>

      <Dialog
        open={Boolean(removeTarget)}
        onClose={() => !removing && setRemoveTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>Remove Collaborator</Typography>
          <IconButton size="small" onClick={() => setRemoveTarget(null)} disabled={removing}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ pt: 0.5 }}>
            Remove <strong>{removeTarget?.username}</strong> from this document?
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
            They will lose access to edit and view this draft.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => setRemoveTarget(null)}
            disabled={removing}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={removing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DeleteOutlineIcon />}
            onClick={handleConfirmRemove}
            disabled={removing}
            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, borderRadius: '8px', minWidth: 140 }}
          >
            {removing ? 'Removing...' : 'Remove Collaborator'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
